import { useCallback, useEffect, useRef, useState } from "react";
import {
  CalendarCheck,
  Crown,
  RefreshCw,
  ShieldCheck,
  UserRoundPlus,
  UsersRound,
  X,
} from "lucide-react";
import { useClinic } from "../context/ClinicContext";
import { useAccess } from "../access/useAccess";
import { ACCESS_ROLES, isClinicalProvider } from "../access/policy";
import {
  cancelClinicInvitation,
  inviteClinicMember,
  listClinicTeam,
  saveClinicMember,
  setClinicMemberState,
} from "./api";
import TeamMemberDialog from "./TeamMemberDialog";
import "./team.css";
const initials = (name) =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
export default function ClinicTeam() {
  const { clinicId, clinicData, refreshClinic } = useClinic(),
    { can } = useAccess();
  const [members, setMembers] = useState([]),
    [invitations, setInvitations] = useState([]),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false);
  const [error, setError] = useState(""),
    [dialogError, setDialogError] = useState(""),
    [notice, setNotice] = useState(""),
    [editing, setEditing] = useState(undefined),
    [agenda, setAgenda] = useState(false),
    [showInactive, setShowInactive] = useState(false);
  const generation = useRef(0),
    mounted = useRef(true),
    currentClinic = useRef(clinicId);
  currentClinic.current = clinicId;
  const clinicName =
    clinicData?.nombre || clinicData?.nombreClinica || "el consultorio";
  const canRead = can("users"),
    canInvite = can("users", "invite"),
    canEdit = can("users", "update"),
    canPermissions = can("users", "permissions"),
    canSuspend = can("users", "suspend");
  const load = useCallback(async () => {
    if (!clinicId || !canRead) {
      setLoading(false);
      return;
    }
    const request = ++generation.current;
    setLoading(true);
    setError("");
    try {
      const result = await listClinicTeam(clinicId);
      if (!mounted.current || request !== generation.current) return;
      setMembers(result.members || []);
      setInvitations(result.invitations || []);
    } catch (caught) {
      if (mounted.current && request === generation.current)
        setError(caught.message || "No se pudo cargar el equipo.");
    } finally {
      if (mounted.current && request === generation.current) setLoading(false);
    }
  }, [clinicId, canRead]);
  useEffect(() => {
    mounted.current = true;
    setEditing(undefined);
    setNotice("");
    setMembers([]);
    setInvitations([]);
    load();
    return () => {
      mounted.current = false;
      generation.current++;
    };
  }, [load]);
  const close = useCallback(() => {
    if (!busy) {
      setEditing(undefined);
      setDialogError("");
    }
  }, [busy]);
  const run = async (operation, message) => {
    if (busy) return;
    const scope = clinicId;
    setBusy(true);
    setDialogError("");
    try {
      await operation();
      if (!mounted.current || currentClinic.current !== scope) return;
      setEditing(undefined);
      setNotice(message);
      await load();
      await refreshClinic?.();
    } catch (caught) {
      if (mounted.current && currentClinic.current === scope) {
        setDialogError(caught.message || "No se pudo guardar el cambio.");
        await load();
      }
    } finally {
      if (mounted.current && currentClinic.current === scope) setBusy(false);
    }
  };
  const save = (profile) =>
    run(
      () =>
        editing?.uid && !editing?.invitationId
          ? saveClinicMember(
              clinicId,
              editing.uid,
              profile,
              editing.revision || 0,
            )
          : inviteClinicMember(clinicId, profile),
      editing?.uid && !editing?.invitationId
        ? "Perfil y permisos actualizados."
        : "Invitación enviada. El acceso se activará cuando la persona la acepte.",
    );
  const changeState = (activo) =>
    run(
      () =>
        setClinicMemberState(
          clinicId,
          editing.uid,
          activo,
          editing.revision || 0,
        ),
      activo
        ? "Acceso reactivado en este consultorio."
        : "Acceso desactivado en este consultorio. Sus citas y registros se conservan.",
    );
  const cancel = async (invitation) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await cancelClinicInvitation(clinicId, invitation.invitationId);
      if (mounted.current) {
        setNotice("Invitación cancelada.");
        await load();
      }
    } catch (caught) {
      if (mounted.current) setError(caught.message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  if (!canRead)
    return (
      <div className="clinic-team">
        <p>No tienes permiso para consultar el equipo de este consultorio.</p>
      </div>
    );
  const active = members.filter((member) => member.activo !== false),
    inactive = members.filter((member) => member.activo === false),
    pending = invitations.filter((invitation) =>
      ["pending", "delivery_failed", "sent"].includes(invitation.status),
    ),
    providers = active.filter(isClinicalProvider);
  const rows = [
    ...(showInactive ? inactive : active),
    ...(!showInactive ? pending : []),
  ];
  return (
    <section className="clinic-team" aria-label="Equipo y accesos">
      <header className="team-page-heading">
        <div>
          <p className="team-eyebrow">Equipo · {clinicName}</p>
          <h1>
            <UsersRound size={25} />
            Usuarios
          </h1>
          <p>Tu equipo, sus agendas y accesos en un solo lugar.</p>
        </div>
        {canInvite && (
          <button
            type="button"
            className="team-button team-primary"
            disabled={busy}
            onClick={() => {
              setDialogError("");
              setEditing(null);
            }}
          >
            <UserRoundPlus size={17} />
            Agregar usuario
          </button>
        )}
      </header>
      {notice && (
        <div className="team-success" role="status">
          {notice}
          <button
            type="button"
            aria-label="Cerrar mensaje"
            onClick={() => setNotice("")}
          >
            <X size={15} />
          </button>
        </div>
      )}
      {error && (
        <div className="team-error" role="alert">
          {error}
          <button type="button" className="team-button" onClick={load}>
            Reintentar
          </button>
        </div>
      )}
      <div className="team-list-toolbar">
        <span>
          {active.length} activos · {pending.length} por aceptar
          {inactive.length ? " · " + inactive.length + " desactivados" : ""}
        </span>
        <div className="team-actions">
          {inactive.length > 0 && (
            <button
              type="button"
              className="team-button team-quiet"
              aria-pressed={showInactive}
              onClick={() => setShowInactive((value) => !value)}
            >
              {showInactive ? "Ver equipo activo" : "Ver desactivados"}
            </button>
          )}
          <button
            type="button"
            className="team-button team-quiet"
            disabled={loading || busy}
            aria-label="Actualizar equipo"
            onClick={load}
          >
            <RefreshCw size={15} />
          </button>
          <button
            type="button"
            className="team-button team-quiet"
            aria-expanded={agenda}
            onClick={() => setAgenda((value) => !value)}
          >
            <CalendarCheck size={16} />
            {agenda ? "Cerrar vista de agenda" : "Ver equipo en agenda"}
          </button>
        </div>
      </div>
      {agenda && (
        <div className="team-agenda-card">
          <label>
            Profesional que atiende
            <select aria-label="Profesional que atiende">
              <option value="">Seleccionar profesional</option>
              {providers.map((member) => (
                <option key={member.uid} value={member.uid}>
                  {member.nombreAgenda || member.nombre}
                </option>
              ))}
            </select>
          </label>
          <p className="team-help">
            Integrantes activos que atienden pacientes, incluido el personal
            administrativo.
          </p>
        </div>
      )}
      {loading ? (
        <p role="status" className="team-loading">
          Cargando equipo…
        </p>
      ) : (
        <div className="team-list">
          <div className="team-list-head">
            <span>Integrante</span>
            <span>Perfil de acceso</span>
            <span>Atención y estado</span>
            <span />
          </div>
          {rows.map((member) => {
            const invitation = Boolean(member.invitationId),
              provider = isClinicalProvider(member),
              key = member.uid || member.invitationId;
            return (
              <article className="team-member-row" key={key}>
                <div className="team-person">
                  <span className="team-avatar" aria-hidden="true">
                    {initials(member.nombre)}
                  </span>
                  <div>
                    <strong>{member.nombre || member.email}</strong>
                    <small>{member.email}</small>
                  </div>
                </div>
                <div className="team-access-cell">
                  {ACCESS_ROLES[member.rol] || "Personalizado"}
                  {member.isOwner ? (
                    <span className="team-owner">
                      <Crown size={12} />
                      Propietaria/o
                    </span>
                  ) : (
                    <small>
                      {member.access?.patientScope === "assigned"
                        ? "Pacientes asignados"
                        : "Acceso del consultorio"}
                    </small>
                  )}
                </div>
                <div className="team-clinical-cell">
                  {member.atiendePacientes || provider ? (
                    <>
                      <span>
                        <span
                          className="team-agenda-dot"
                          style={{ background: member.color || "#5578cb" }}
                        />
                        Atiende pacientes
                      </span>
                      <small>{member.especialidad || "Perfil clínico"}</small>
                    </>
                  ) : (
                    <small>Sin agenda clínica</small>
                  )}
                  <span
                    className={
                      "team-status " +
                      (invitation
                        ? "team-status-pending"
                        : member.activo === false
                          ? "team-status-inactive"
                          : "")
                    }
                  >
                    {invitation
                      ? member.status === "delivery_failed"
                        ? "Correo no enviado"
                        : member.expired
                          ? "Invitación vencida"
                          : "Invitación pendiente"
                      : member.activo === false
                        ? "Desactivado"
                        : "Activo"}
                  </span>
                </div>
                <div className="team-row-actions">
                  {(invitation ? canInvite : canEdit || canPermissions) && (
                    <button
                      type="button"
                      className="team-button team-quiet"
                      disabled={busy}
                      aria-label={
                        (invitation ? "Reenviar invitación a " : "Editar a ") +
                        (member.nombre || member.email)
                      }
                      onClick={() => {
                        setDialogError("");
                        setEditing(member);
                      }}
                    >
                      {invitation ? "Reenviar" : "Editar"}
                    </button>
                  )}
                  {invitation && canInvite && (
                    <button
                      type="button"
                      className="team-icon-button"
                      disabled={busy}
                      aria-label={"Cancelar invitación de " + member.email}
                      onClick={() => cancel(member)}
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>
              </article>
            );
          })}
          {!rows.length && (
            <p className="team-empty">
              {showInactive
                ? "No hay integrantes desactivados."
                : "Aún no hay integrantes. Agrega el primero con una invitación."}
            </p>
          )}
        </div>
      )}
      <p className="team-scope-note">
        <ShieldCheck size={15} />
        Los permisos y la atención clínica se configuran por separado para este
        consultorio.
      </p>
      {editing !== undefined && (
        <TeamMemberDialog
          key={editing?.uid || editing?.invitationId || "new"}
          member={editing}
          clinicName={clinicName}
          busy={busy}
          error={dialogError}
          canEditPermissions={canPermissions}
          canEditProfile={canEdit}
          canSuspend={canSuspend}
          onClose={close}
          onSave={save}
          onState={changeState}
        />
      )}
    </section>
  );
}
